import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';
import Grid from '@material-ui/core/Grid';
import DefaultTable from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import DefaultTableCell from '@material-ui/core/TableCell';
import Typography from '@material-ui/core/Typography';
import CellActions from './CellActions';

const propTypes = {
  actions: PropTypes.shape({}),
  data: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  headers: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  isLoading: PropTypes.bool,
  verticalAlign: PropTypes.oneOf(['top', 'middle']),
  from: PropTypes.number,
};

const defaultProps = {
  actions: {},
  isLoading: false,
  verticalAlign: 'middle',
  from: 0,
};

const useStyles = makeStyles(() => ({
  root: {
    overflowX: 'auto',
  },
}));

const useCellStyles = makeStyles(() => ({
  top: {
    verticalAlign: 'top',
  },
  middle: {
    verticalAlign: 'middle',
  },
}));

const TableCell = ({ verticalAlign, ...others }) => {
  const classes = useCellStyles();
  return <DefaultTableCell classes={{ root: classes[verticalAlign] }} {...others} />;
};

TableCell.propTypes = {
  verticalAlign: PropTypes.oneOf(['top', 'middle']).isRequired,
};

function Table(props) {
  const classes = useStyles();
  const {
    headers, data, actions, isLoading, verticalAlign, from,
  } = props;

  function renderTableHead() {
    const cells = headers.map((el, index) => (
      // eslint-disable-next-line react/no-array-index-key
      <TableCell key={index} style={{ width: el.width || null }} verticalAlign={verticalAlign}>
        {el.label}
      </TableCell>
    ));

    return (
      <TableHead>
        <TableRow>{cells}</TableRow>
      </TableHead>
    );
  }

  function renderTableBody() {
    if (isLoading) {
      return (
        <TableBody>
          <TableRow>
            <TableCell colSpan={headers.length} verticalAlign={verticalAlign}>
              <Typography align="center">Cargando...</Typography>
            </TableCell>
          </TableRow>
        </TableBody>
      );
    }

    if (data.length === 0) {
      return (
        <TableBody>
          <TableRow>
            <TableCell colSpan={headers.length} verticalAlign={verticalAlign}>
              <Typography align="center">Sin datos.</Typography>
            </TableCell>
          </TableRow>
        </TableBody>
      );
    }

    function getPen(n) {
      return "S/ "+((n)*1).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    }

    function getPercentage(n) {
      return Number(n/1).toLocaleString(undefined, {style: 'percent', minimumFractionDigits: 1});
    }

    const content = (row, cell, pos) => {
      if (cell.name === '__sequence') {
        return from + pos;
      }
      if (typeof cell.show !== 'undefined') {
        const showCell = cell.show(row, pos);
        if (!showCell) {
          return null;
        }
      }
      if (cell.actions) {
        return <CellActions buttons={cell.actions} index={pos} row={row} actions={actions} />;
      }
      if (!cell.component) {
        if (cell.name === 'approved_amount') {
          return getPen(row[cell.name]);
        } else if (cell.name === 'interest') {
          return getPercentage(row[cell.name]);
        } else {
          return row[cell.name];
        }
      }
      return cell.component(row[cell.name], row, pos, actions);
    };

    const cells = (row, pos) => headers.map((cell, index) => (
      // eslint-disable-next-line react/no-array-index-key
      <TableCell key={index} verticalAlign={verticalAlign}>
        {content(row, cell, pos)}
      </TableCell>
    ));

    const rows = data.map((row, index) => (
      row
        ? (
          // eslint-disable-next-line react/no-array-index-key
          <TableRow key={index}>
            {cells(row, index)}
          </TableRow>
        )
        : null
    ));

    return (
      <TableBody>{rows}</TableBody>
    );
  }

  const tableHead = renderTableHead();

  const tableBody = renderTableBody();

  return (
    <Grid item classes={{ root: classes.root }}>
      <DefaultTable>
        {tableHead}
        {tableBody}
      </DefaultTable>
    </Grid>
  );
}

Table.propTypes = propTypes;
Table.defaultProps = defaultProps;

export default Table;
